import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  ThemeProvider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { leavePalette, cardSx, statusMeta } from "../Leave/leaveHelpers";
import { RequestStatusCards } from "../RequestStatusCards";
import { CreateDayOff } from "./OffDayPreApproval/CreateDayOff";
import { EditDayOff } from "./OffDayPreApproval/EditDayOff";
import { ViewDayOffPostApproval } from "./OffDayPostApproval/ViewDayOffPostApproval";

const canonicalStatus = (status) => {
  const k = (status || "").trim().toLowerCase();
  if (k === "completed") return "Completed";
  if (k === "approved") return "Approved";
  if (k === "pending") return "Pending";
  if (k === "rejected") return "Rejected";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  return status;
};

const dateFormats = ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601];
const dateTimeFormats = [
  "DD-MM-YYYY, HH:mm",
  "DD-MM-YYYY, hh:mm A",
  "YYYY-MM-DD HH:mm:ss",
  moment.ISO_8601,
];

const parseMoment = (value, formats = dateFormats) => {
  if (!value) return null;
  const parsed = moment(value, formats, true);
  if (parsed.isValid()) return parsed;
  const fallback = moment(value);
  return fallback.isValid() ? fallback : null;
};

const formatOffDayDate = (value) => {
  const parsed = parseMoment(value);
  return parsed ? parsed.format("DD-MM-YYYY") : value || "-";
};

const formatOffDayDateTime = (value) => {
  const parsed = parseMoment(value, dateTimeFormats);
  return parsed ? parsed.format("DD MMM YYYY, hh:mm A") : value || "-";
};

const toIsoDate = (value) => {
  const parsed = parseMoment(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
};

const stripEmployeeIdFromName = (name, employeeId) => {
  const text = (name || "").trim();
  if (!text || !employeeId) return text;
  return text.replace(new RegExp(`\\s*[-|/]?\\s*${employeeId}\\s*$`, "i"), "").trim();
};

const resolveUserName = (userDetails) =>
  stripEmployeeIdFromName(
    userDetails?.first_name || userDetails?.user_name || userDetails?.username,
    userDetails?.employee_id,
  ) || "N/A";

const resolveDisplayStatus = (status, isoDate) => {
  const canonical = canonicalStatus(status);
  if (
    canonical === "Approved" &&
    isoDate &&
    moment(isoDate, "YYYY-MM-DD", true).isBefore(moment(), "day")
  ) {
    return "Completed";
  }
  return canonical;
};

// Typed history columns feed both the shared CustomFilter (search / column
// filters / sort) and the SavedViewSelector "Default View" dropdown, exactly
// like My Attendance.
const HEADERS = [
  { name: "Duty Type", key: "dutyType", type: "autocomplete", icon: <CategoryOutlinedIcon fontSize="small" /> },
  { name: "Approval Mode", key: "mode", type: "autocomplete", icon: <RuleOutlinedIcon fontSize="small" /> },
  { name: "Date", key: "date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  { name: "Reason", key: "reason", type: "string", icon: <SubjectOutlinedIcon fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
];

// Clicking a tile filters the table to that status; the "All" tile (empty
// filter value) shows the grand total and clears any active filter.
// Off Day Duty is the one My Requests screen with a "Completed" status (an
// Approved request whose duty date has passed - see resolveDisplayStatus), so
// it passes its own card list; same order as Team Workspace > Approvals >
// Off Day Duty's cards.
const STATUS_CARD_KEYS = ["All", "Pending", "Approved", "Completed", "Rejected", "Cancelled"];

const headCellSx = {
  color: leavePalette.text,
  fontWeight: 700,
  fontSize: 12,
  bgcolor: "#fff",
  borderBottom: `1px solid ${leavePalette.border}`,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: `1px solid ${leavePalette.border}`,
  fontSize: 13,
  color: leavePalette.text,
};

// Redesigned My Zone > My Requests > Off-Day Duty APPLY screen (image 6). The
// two approval stages (Planned before the off-day / Retrospective after) are
// merged into one list tagged by an Approval Mode chip, so the employee sees
// all their own off-day duty requests together. Applying runs through the
// existing CreateDayOff drawer.
export const OffDayDuty = ({ subModuleId }) => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  const [statusFilter, setStatusFilter] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [updateData, setUpdateData] = useState(false);
  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);

  // CustomFilter output + pagination.
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Myzone Off Day Duty",
  );

  // Unified Off-Day Duty endpoint: returns Pre-Dayoff and Post-Dayoff in one
  // list, each row tagged with `dayoff_type` ("PreDayOff" / "PostDayOff").
  // Replaces the previous two calls to myzone-pre-day-off/ and
  // myzone-post-day-off/.
  const {
    result: dayOffData,
    setLoad: reCallDayOff,
    loading: dayOffLoading,
  } = useFetch(
    `${domain}my_zone/apply/day_off/myworkspace-dayoff-approval/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallDayOff(true);
    }
  }, [reCallDayOff, topbarCompanyId]);

  // Split the unified list by dayoff_type into the same row shapes the table
  // uses: Pre-Dayoff -> Planned mode, Post-Dayoff -> Retrospective mode.
  const rows = useMemo(() => {
    const list = Array.isArray(dayOffData) ? dayOffData : [];
    const map = (arr, mode) =>
      arr.map((r) => {
        const isoDate = toIsoDate(r.date || r.requested_date);
        const appliedRaw =
          r.applied_on || r.requested_on || r.created_at || r.created_on;
        const status = resolveDisplayStatus(r.status, isoDate);
        return {
          raw: { ...r, status },
          mode,
          dutyType: r.day_type || "Off-Day Duty",
          date: formatOffDayDate(r.date || r.requested_date),
          dateIso: isoDate,
          applied: formatOffDayDateTime(appliedRaw),
          appliedIso: toIsoDate(appliedRaw),
          reason: r.reason || r.applied_reason || r.description || "-",
          rejected_reason: r.rejected_reason,
          cancelled_reason: r.cancelled_reason,
          status,
          color: r.color_code,
        };
      });
    return [
      ...map(
        list.filter((r) => r.dayoff_type === "PreDayOff"),
        "Planned",
      ),
      ...map(
        list.filter((r) => r.dayoff_type === "PostDayOff"),
        "Retrospective",
      ),
    ];
  }, [dayOffData]);

  const dateFilteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (!row.dateIso) return true;
        return row.dateIso >= startDate && row.dateIso <= endDate;
      }),
    [endDate, rows, startDate],
  );

  const counts = useMemo(() => {
    const c = { Pending: 0, Approved: 0, Completed: 0, Rejected: 0, Cancelled: 0 };
    dateFilteredRows.forEach((r) => {
      const s = canonicalStatus(r.status);
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [dateFilteredRows]);

  // Rows fed to CustomFilter: pre-filtered by the clickable status tiles. The
  // shared filter/search/sort then reads the flat row[key] columns directly.
  const filterInputRows = useMemo(
    () =>
      dateFilteredRows.filter(
        (r) => !statusFilter || canonicalStatus(r.status) === statusFilter,
      ),
    [dateFilteredRows, statusFilter],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_offday_duty_saved_views",
    fullHeaders: HEADERS,
    defaultHeaderKeys: ["dutyType", "mode", "date", "applied", "reason", "status"],
    screenAccess: checkAccess,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name": resolveUserName(userDetails),
      Type: "Off-Day Duty",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  const openApply = () => {
    if (checkAccess) setIsCreateOpen(true);
    else
      showToast(
        "error",
        "Access denied: You are not authorized to apply for off-day duty",
      );
  };

  // Planned (pre-approval) rows open the edit/view drawer; Retrospective
  // (post-approval) rows are read-only, so they open the plain view drawer.
  const openRow = (row) => {
    setSelectedRow(row.raw);
    if (row.mode === "Planned") {
      setUpdateData(true);
      setIsEditOpen(true);
    } else {
      setIsViewOpen(true);
    }
  };

  const renderCell = (row, head) => {
    switch (head.key) {
      case "mode": {
        const planned = row.mode === "Planned";
        const ModeIcon = planned ? ScheduleIcon : HistoryToggleOffIcon;
        const modeColor = planned ? leavePalette.blue : leavePalette.amber;
        return (
          <Chip
            size="small"
            icon={<ModeIcon sx={{ fontSize: 16, color: `${modeColor} !important` }} />}
            label={row.mode}
            sx={{
              bgcolor: `${modeColor}14`,
              color: modeColor,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          />
        );
      }
      case "status": {
        const color = statusMeta(row.status, row.color);
        return (
          <Chip
            size="small"
            label={row.status || "N/A"}
            sx={{
              bgcolor: `${color}1A`,
              color,
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          />
        );
      }
      case "dutyType":
      case "date":
      case "applied":
        return (
          <Typography variant="body2" sx={{ color: leavePalette.text }}>
            {row[head.key]}
          </Typography>
        );
      case "reason":
        return (
          <Typography variant="body2" sx={{ color: leavePalette.subText }}>
            {row.reason}
          </Typography>
        );
      default:
        return <Typography variant="body2">{row[head.key] ?? "-"}</Typography>;
    }
  };

  const OffDayDutyMobileCard = ({ row }) => {
    const statusHeader = visibleHeaders.find((head) => head.key === "status");
    const detailHeaders = visibleHeaders.filter((head) => head.key !== "status");

    return (
      <Box
        onClick={() => openRow(row)}
        sx={{
          p: 1.5,
          border: `1px solid ${leavePalette.border}`,
          borderRadius: 2,
          backgroundColor: "#fff",
          cursor: "pointer",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: leavePalette.text,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.dutyType}
            </Typography>
            {statusHeader ? renderCell(row, statusHeader) : null}
          </Stack>
          {detailHeaders.map((head) => (
            <Stack
              key={head.key}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              sx={{ minWidth: 0 }}
            >
              <Typography sx={{ fontSize: 12, color: leavePalette.subText }}>
                {head.name}
              </Typography>
              <Box
                sx={{
                  fontSize: 12.5,
                  color: leavePalette.text,
                  fontWeight: 600,
                  maxWidth: "58%",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {renderCell(row, head)}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  };

  if (dayOffLoading) return <Loader />;

  return (
    <React.Fragment>
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: leavePalette.text }}>
              Off-Day Duty
            </Typography>
            <Typography variant="body2" sx={{ color: leavePalette.subText }}>
              Apply for week off / holiday work and track your requests
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={(rangeStart, rangeEnd, rangeLabel) => {
                setStartDate(moment(rangeStart).format("YYYY-MM-DD"));
                setEndDate(moment(rangeEnd).format("YYYY-MM-DD"));
                setDateRangeLabel(rangeLabel || "Custom Range");
                setPage(0);
              }}
              includeCurrentDate
              showToday
            />
            {checkAccess ? (
              <PrimaryButton
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: { xs: 0, sm: HEADER_ACTION_BUTTON_WIDTH },
                  width: { xs: "100%", sm: "auto" },
                }}
                onClick={openApply}
              >
                New Request
              </PrimaryButton>
            ) : null}
          </Stack>
        </Stack>

        {/* Status cards (quick filters) - same shared cards as Team Workspace > Approvals */}
        <RequestStatusCards
          keys={STATUS_CARD_KEYS}
          counts={counts}
          total={dateFilteredRows.length}
          activeStatus={statusFilter}
          onSelect={(value) => {
            setStatusFilter(value);
            setPage(0);
          }}
        />

        {/* Table card - wrapped in the design-system font (Poppins) so every
            head/body cell, chip and label reads with the same typography as the
            rest of the redesigned modules, per designTokens.js. */}
        <ThemeProvider theme={withDesignFont}>
        <Box sx={{ ...cardSx, p: 2.25 }}>
          {/* My Attendance-style toolbar: Default View · Search · Download · Sort · Filters */}
          <CustomFilter
            title="Off-Day Duty"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={filterInputRows}
            filteredData={tableRows}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={checkAccess}
            showSaveButton={true}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
            searchPlaceholder="Search by duty type, reason or date..."
          />

          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            {paginatedRows.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: leavePalette.subText,
                  border: `1px solid ${leavePalette.border}`,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                }}
              >
                No off-day duty requests found.
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {paginatedRows.map((row, index) => (
                  <OffDayDutyMobileCard
                    key={`${row.mode}-${row.raw?.id ?? index}`}
                    row={row}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  {visibleHeaders.map((head) => (
                    <TableCell key={head.key} sx={headCellSx}>
                      {head.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableEmptyState
                    colSpan={visibleHeaders.length || 1}
                    dataLength={paginatedRows}
                    title="No Off Day Duty Records Found"
                    subtitle="There's nothing to show here yet."
                  />
                ) : (
                  paginatedRows.map((r, i) => {
                    return (
                      <TableRow
                        key={`${r.mode}-${r.raw?.id ?? i}`}
                        hover
                        onClick={() => openRow(r)}
                        sx={{ cursor: "pointer" }}
                      >
                        {visibleHeaders.map((head) => (
                          <TableCell key={head.key} sx={bodyCellSx}>
                            {renderCell(r, head)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${leavePalette.border}` }}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: tableRows.length,
              itemLabel: "records",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
        </ThemeProvider>
      </Box>

      {/* Apply panel */}
      {isCreateOpen ? (
        <CreateDayOff
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          reCallApi={(v) => reCallDayOff(v)}
        />
      ) : null}

      {/* Planned (pre-approval) view/edit panel */}
      {isEditOpen ? (
        <EditDayOff
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          setUpdateData={setUpdateData}
          updateData={updateData}
          selectedData={selectedRow}
          reCallApi={(v) => reCallDayOff(v)}
          checkAccess={checkAccess}
        />
      ) : null}

      {/* Retrospective (post-approval) view panel */}
      {isViewOpen ? (
        <ViewDayOffPostApproval
          open={isViewOpen}
          selectedData={selectedRow}
          onClose={() => setIsViewOpen(false)}
          onUpdated={() => reCallDayOff(true)}
          checkAccess={checkAccess}
        />
      ) : null}
    </React.Fragment>
  );
};
